/* Network — handover §14 step 8, and one of the four things pinned for offline.

   §7 rule 4: this is what an incident actually needs. If the Hull circuit is
   down, the person reading this screen is standing in a comms room on a phone
   with no working network, and everything they need has to already be on the
   device — the circuit reference to quote, the support number to ring, the
   gateway to check.

   So it reads as a document rather than a table of tables: one depot at a time,
   circuit details first because that is what an outage is about, then
   addressing, then wireless, then the tunnels. Nothing is behind a click that
   an incident would need, because a drawer that fetches is a drawer that fails
   at exactly the wrong moment.

   Unlike every other register here this one is genuinely per-depot, so it
   follows the header's depot scope instead of ignoring it. */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDepotScope } from '../../hooks/useDepotScope';
import { useDepots } from '../../hooks/useDepots';
import { useOnline } from '../../hooks/useOnline';
import { expiryTone, fmtDate } from '../../lib/format';
import type { DepotId, INetwork } from '../../models';
import { duplicateIps, subnetUsage } from '../../lib/network';
import { recordHref } from '../../hooks/useOpenRecord';
import { groupOf } from '../../lib/deviceGroups';
import { FileDrop } from './FileDrop';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import {
  Badge,
  Button,
  Controls,
  DataTable,
  EmptyState,
  KeyValue,
  LoadError,
  Panel,
  Segmented,
  TableSkeleton,
  type Column,
} from '../ui';
import { Copyable } from './Copyable';
import { EditDrawer } from './EditDrawer';
import { NestedList } from './NestedList';
import { networkFields, valuesSeen } from '../../lib/registerFields';
import s from './screens.module.scss';
import { PRODUCT } from '../../lib/product';

/* Offered, not enforced — same reasoning as the lists in registerFields.ts.
   The security on a real SSID here reads "WPA2-Enterprise (RADIUS via NPS over
   tunnel)", which no dropdown would have let anybody record. */
const WIFI_SECURITY = [
  'WPA2-PSK',
  'WPA3-Personal',
  'WPA2/WPA3 mixed',
  'WPA2-Enterprise (RADIUS via NPS)',
  'WPA3-Enterprise',
  'Open — captive portal',
  'Open',
];

const TUNNEL_TYPE = [
  'IPsec site-to-site',
  'SSL VPN (client)',
  'Entra Private Access',
  'WireGuard',
  'OpenVPN',
  'L2TP/IPsec',
  'Azure point-to-site',
];

const TUNNEL_STATUS = ['Up', 'Down', 'Standby', 'Degraded', 'Decommissioned'];

export function NetworkScreen() {
  const svc = useServices();
  const online = useOnline();
  const { scope, setScope } = useDepotScope();

  const depots = useDepots();
  const networks = useQuery({ queryKey: qk.networks, queryFn: () => svc.networks.all() });
  const locations = useQuery({ queryKey: qk.locations, queryFn: () => svc.locations.all() });
  /* Only so the wireless form can offer the credential entries by name. §16
     holds: the register knows which entry holds a key, never the key. */
  const credentials = useQuery({ queryKey: qk.credentials, queryFn: () => svc.credentials.list() });

  /* The header's "All depots" is not a useful view of a network — the subnets
     of three sites in one table would be actively misleading during an
     incident. So the screen keeps its own selection, seeded from the scope. */
  const [shown, setShown] = useState<DepotId | null>(null);
  const depot: DepotId = shown ?? (scope !== 'all' ? (scope as DepotId) : (depots[0]?.id as DepotId));

  /* Offered on the wireless form. Both are answers the register already holds,
     which beats anything that could be listed here in advance — and it means
     the same controller is spelled one way across three depots. */
  const credentialNames = (credentials.data ?? []).map((c) => c.name).sort((a, b) => a.localeCompare(b));
  const subnetResolvers = [
    ...new Set([
      ...Object.values(networks.data ?? {})
        .flatMap((n) => n.subnets.map((x) => x.dns))
        .filter((x): x is string => Boolean(x && x.trim())),
      '1.1.1.1, 1.0.0.1',
      '8.8.8.8, 8.8.4.4',
    ]),
  ];

  const apControllers = [
    ...new Set(
      Object.values(networks.data ?? {})
        .flatMap((n) => n.ssids.map((x) => x.ap))
        .filter((x): x is string => Boolean(x && x.trim())),
    ),
  ].sort((a, b) => a.localeCompare(b));

  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const save = useMutation({

    meta: { ok: 'Network saved' },
    mutationFn: (patch: Partial<INetwork>) => svc.networks.save(depot, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.networks });
      void qc.invalidateQueries({ queryKey: qk.audit });
      setEditing(false);
    },
  });

  /* Devices carry an address and subnets carry a block; nothing joined them
     until now, so "what is 10.10.10.57" had no answer. This is the join, and it
     is the one thing Hudu names that IT Glue cannot do. */
  const devices = useQuery({ queryKey: qk.devices, queryFn: () => svc.devices.list() });

  /* The machines that could plausibly be handing out leases, offered on the
     subnet form. Infrastructure rather than the whole register: nobody runs
     DHCP off a laptop, and sixty endpoints in the list would bury the four
     that matter. */
  const serverTags = (devices.data ?? [])
    .filter((d) => groupOf(d.type) === 'infrastructure')
    .map((d) => d.tag)
    .sort((a, b) => a.localeCompare(b));

  /* Diagrams and floor plans, filed against network-<depot> in the files
     library. IT Glue's LAN asset carries both, for the good reason that a
     diagram is what you actually want while standing in a comms room. */
  const files = useQuery({
    queryKey: qk.attachments(`network-${depot}`),
    queryFn: () => svc.attachments.forRecord(`network-${depot}`),
  });

  const net: INetwork | undefined = networks.data?.[depot];
  const depotMeta = useMemo(() => depots.find((d) => d.id === depot), [depots, depot]);

  /* Everything the address panel needs, worked out once. Scoped to the depot by
     the subnets themselves rather than by a device's Depot column — where a
     machine answers is a better answer than where somebody filed it. */
  const addressing = useMemo(() => {
    const subnets = net?.subnets ?? [];
    const all = devices.data ?? [];
    const rows = subnets.map((subnet) => ({ subnet, usage: subnetUsage(subnet, all) }));
    const hosts = rows.flatMap((r) => r.usage.hosts.map((h) => ({ ...h, subnet: r.subnet })));
    const ids = new Set(hosts.map((h) => h.device.id));
    return {
      bySubnet: new Map(rows.map((r) => [r.subnet.cidr, r.usage])),
      hosts,
      /* Only clashes inside this depot. Two sites reusing 10.x is a design, not
         a fault, and reporting it as one would train people to ignore this. */
      clashes: duplicateIps(all.filter((d) => ids.has(d.id))),
    };
  }, [net, devices.data]);

  const subnetColumns: Column<INetwork['subnets'][number]>[] = [
    { key: 'vlan', header: 'VLAN', width: '80px', sortBy: (x) => x.vlan, render: (x) => <span className={s.mono}>{x.vlan}</span> },
    { key: 'name', header: 'Name', sortBy: (x) => x.name, render: (x) => x.name },
    { key: 'cidr', header: 'Range', sortBy: (x) => x.cidr, render: (x) => <Copyable value={x.cidr} /> },
    { key: 'gw', header: 'Gateway', sortBy: (x) => x.gw, render: (x) => <Copyable value={x.gw} /> },
    {
      key: 'dhcp',
      header: 'DHCP',
      sortBy: (x) => (x.dhcp ? 0 : 1),
      /* A tick and the scope in one cell. "Yes" on its own answers half the
         question — the next thing anybody asks is what it hands out. */
      render: (x) =>
        x.dhcp ? (
          <>
            <Badge tone="ok">Yes</Badge>
            {x.dhcpRange && <span className={s.mono}> {x.dhcpRange}</span>}
          </>
        ) : (
          <Badge tone="muted">No</Badge>
        ),
    },
    {
      key: 'used',
      /* "3 of 510 in use" was a claim this register cannot make.
       *
       * It counted the addresses written on device records, which is not
       * occupancy — nothing here reads a DHCP lease table, so a subnet with
       * ninety machines on it and three of them documented said 3. Put beside
       * the capacity it read as "507 free", which was wrong by every machine
       * nobody had got round to recording.
       *
       * So it says what it counted. The capacity moves to the line underneath,
       * where it is a planning figure rather than the other half of a fraction,
       * and the tooltip says plainly where the number comes from. */
      header: 'Recorded',
      sortBy: (x) => -(addressing.bySubnet.get(x.cidr)?.hosts.length ?? 0),
      render: (x) => {
        const usage = addressing.bySubnet.get(x.cidr);
        if (!usage || usage.usable === 0) return <span className={s.cellSub}>—</span>;
        return (
          <span
            className={s.cellSub}
            title={`${PRODUCT.name} counts the addresses written on device records. It does not read DHCP leases, so this is what is documented on this subnet, not what is switched on. The subnet itself holds ${usage.usable} usable addresses.`}
          >
            <span className={s.mono}>{usage.hosts.length}</span>
            {usage.staticCount > 0 && ` · ${usage.staticCount} static`}
            <span className={s.cellSub}> · {usage.usable} usable</span>
          </span>
        );
      },
    },
    {
      key: 'dns',
      header: 'DNS',
      // Blank means the site default, which is the common case — saying so beats
      // repeating the same pair of addresses down every row.
      render: (x) =>
        x.dns ? <Copyable value={x.dns} /> : <span className={s.cellSub}>Site default</span>,
    },
    { key: 'note', header: 'Note', render: (x) => <span className={s.cellSub}>{x.note}</span> },
  ];

  const ssidColumns: Column<INetwork['ssids'][number]>[] = [
    { key: 'name', header: 'SSID', sortBy: (x) => x.name, render: (x) => <span className={s.cellMain}>{x.name}</span> },
    { key: 'vlan', header: 'VLAN', render: (x) => <span className={s.mono}>{x.vlan}</span> },
    { key: 'auth', header: 'Security', render: (x) => x.auth },
    { key: 'ap', header: 'Served by', render: (x) => <span className={s.cellSub}>{x.ap}</span> },
    { key: 'purpose', header: 'Purpose', render: (x) => <span className={s.cellSub}>{x.purpose}</span> },
    {
      key: 'cred',
      header: 'Key',
      /* Never the key itself — §16. This names the credentials entry, and the
         vault holds the value. */
      render: (x) =>
        x.cred ? (
          <span className={s.cellSub}>{x.cred}</span>
        ) : (
          <span className={s.cellSub}>Not recorded</span>
        ),
    },
  ];

  const vpnColumns: Column<INetwork['vpn'][number]>[] = [
    { key: 'name', header: 'Tunnel', sortBy: (x) => x.name, render: (x) => <span className={s.cellMain}>{x.name}</span> },
    { key: 'type', header: 'Type', render: (x) => x.type },
    { key: 'dest', header: 'Destination', render: (x) => <span className={s.mono}>{x.dest}</span> },
    { key: 'purpose', header: 'Purpose', render: (x) => <span className={s.cellSub}>{x.purpose}</span> },
    {
      key: 'status',
      header: 'Status',
      render: (x) => (
        <Badge tone={/up|connected|ok/i.test(x.status) ? 'ok' : /down|fail/i.test(x.status) ? 'bad' : 'muted'}>
          {x.status}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <div className={s.head}>
        <div className={s.grow}>
          <h1>Network</h1>
          <p>
            Circuits, addressing, wireless and tunnels, one depot at a time. Available offline.
          </p>
        </div>
        <Button primary icon="ti-pencil" onClick={() => setEditing(true)} disabled={!online}>
          Edit circuit
        </Button>
      </div>

      {networks.isError && <LoadError error={networks.error} what="the network" />}

      <Controls
        count={
          online ? undefined : (
            <>
              <i className="ti ti-wifi-off" aria-hidden /> Cached copy
            </>
          )
        }
      >
        <Segmented
          label="Depot"
          value={depot}
          onChange={(v) => {
            setShown(v as DepotId);
            /* Keep the header in step. Somebody who picks Doncaster here means
               it everywhere, and leaving the two disagreeing is how you read
               Hull's gateway off a screen headed Doncaster. */
            setScope(v as DepotId);
          }}
          options={depots.map((d) => ({ value: d.id, label: d.name }))}
        />
      </Controls>

      {networks.isLoading ? (
        <Panel flush>
          <TableSkeleton rows={8} />
        </Panel>
      ) : !net ? (
        <Panel flush>
          <EmptyState icon="ti-network" title={`No network recorded for ${depotMeta?.name ?? 'this depot'}`}>
            Nothing recorded for this depot yet — "Edit circuit" above starts it.
          </EmptyState>
        </Panel>
      ) : (
        <div className={s.stack}>
          {/* Circuit first. An outage is about the circuit, and the reference
              and the support number are what the call needs. */}
          <Panel
            title={`${depotMeta?.name ?? depot} — circuit`}
            sub={locations.data?.[depot]?.addr || undefined}
            actions={
              net.wanEnds ? (
                <Badge tone={expiryTone(net.wanEnds)}>Contract ends {fmtDate(net.wanEnds)}</Badge>
              ) : undefined
            }
          >
            <KeyValue
              rows={[
                { key: 'Circuit', value: net.wan },
                { key: 'Account', value: net.wanAcct ? <Copyable value={net.wanAcct} /> : '' },
                { key: 'Support', value: net.wanSupport ? <Copyable value={net.wanSupport} /> : '' },
                { key: 'Failover', value: net.failover },
                { key: 'Address block', value: net.block ? <Copyable value={net.block} /> : '' },
                { key: 'DNS', value: net.dns ? <Copyable value={net.dns} /> : '' },
              ]}
            />
          </Panel>

          <NestedList
            title="Subnets"
            sub={`${net.subnets.length} VLANs`}
            label="subnet"
            icon="ti-router"
            emptyTitle="No subnets recorded"
            rows={net.subnets}
            blank={{
              vlan: 0,
              name: '',
              cidr: '',
              gw: '',
              dhcp: false,
              dhcpRange: '',
              dhcpServer: '',
              dns: '',
              note: '',
            }}
            columns={subnetColumns}
            rowKey={(x) => String(x.vlan)}
            initialSort={{ key: 'vlan', dir: 'asc' }}
            fields={[
              { section: 'Addressing', id: 'vlan', label: 'VLAN', type: 'number', required: true },
              { section: 'Addressing', id: 'name', label: 'Name', type: 'text', required: true },
              {
                section: 'Addressing',
                id: 'cidr',
                label: 'Subnet',
                type: 'text',
                hint: '10.10.10.0/24',
              },
              { section: 'Addressing', id: 'gw', label: 'Gateway', type: 'text' },
              {
                section: 'DHCP',
                id: 'dhcp',
                label: 'Serves DHCP',
                type: 'check',
              },
              {
                section: 'DHCP',
                id: 'dhcpRange',
                label: 'Scope',
                type: 'text',
                hint: 'From and to \u2014 "10.10.10.100 \u2013 .200".',
              },
              {
                section: 'DHCP',
                id: 'dhcpServer',
                label: 'DHCP server',
                type: 'text',
                /* The servers and firewalls on the register — whatever hands out
                   leases here is already one of them, and typing its tag by hand
                   is how a subnet ends up pointing at a machine that does not
                   exist under that spelling. */
                suggest: serverTags,
                hint: 'What hands out the leases.',
              },
              {
                section: 'Resolution',
                id: 'dns',
                label: 'DNS servers',
                type: 'text',
                // What the other subnets here resolve against, then the public ones.
                suggest: subnetResolvers,
                hint: 'Leave blank to use the site default.',
              },
              { section: 'Resolution', id: 'note', label: 'Note', type: 'textarea' },
            ]}
            onSave={(subnets) => save.mutate({ subnets })}
            saving={save.isPending}
            disabled={!online}
          />

          <NestedList
            title="Wireless"
            sub={`${net.ssids.length} SSIDs`}
            label="SSID"
            icon="ti-wifi"
            emptyTitle="No SSIDs recorded"
            rows={net.ssids}
            blank={{ name: '', vlan: '', auth: '', ap: '', purpose: '', cred: '' }}
            columns={ssidColumns}
            rowKey={(x) => x.name}
            fields={[
              { id: 'name', label: 'SSID', type: 'text', required: true },
              {
                id: 'vlan',
                label: 'VLAN',
                type: 'text',
                /* The VLANs this depot actually has, from the subnet list above.
                   Typing one that does not exist is the mistake worth making
                   hard, and the answer was already on the page. */
                suggest: net.subnets.map((x) => String(x.vlan)),
                hint: 'The VLAN this SSID lands on, from the subnets above.',
              },
              {
                id: 'auth',
                label: 'Security',
                type: 'text',
                suggest: WIFI_SECURITY,
                hint: 'Pick one or describe it.',
              },
              {
                id: 'ap',
                label: 'Access points or controller',
                type: 'text',
                suggest: apControllers,
              },
              { id: 'purpose', label: 'Purpose', type: 'text' },
              {
                id: 'cred',
                label: 'Key held in',
                type: 'text',
                /* The credential names already on the register. §16 stands: this
                   is which entry holds the key, never the key. */
                suggest: credentialNames,
                /* The one place this deliberately parts company with IT Glue,
                   which stores the pre-shared key on the record itself. */
                hint: 'The name of the credentials entry, never the key itself.',
              },
            ]}
            onSave={(ssids) => save.mutate({ ssids })}
            saving={save.isPending}
            disabled={!online}
          />

          <NestedList
            title="VPN and tunnels"
            sub={`${net.vpn.length} configured — site-to-site links and remote access`}
            label="tunnel"
            icon="ti-lock"
            emptyTitle="No tunnels recorded"
            rows={net.vpn}
            blank={{ name: '', type: '', dest: '', purpose: '', status: '' }}
            columns={vpnColumns}
            rowKey={(x) => x.name}
            fields={[
              { id: 'name', label: 'Tunnel', type: 'text', required: true },
              {
                id: 'type',
                label: 'Type',
                type: 'text',
                suggest: TUNNEL_TYPE,
                hint: 'A site-to-site link, or the remote-access VPN.',
              },
              {
                id: 'dest',
                label: 'Destination',
                type: 'text',
                hint: 'The far end \u2014 a network like 10.20.0.0/16, or the service it reaches.',
              },
              {
                id: 'purpose',
                label: 'Who uses it',
                type: 'text',
              },
              { id: 'status', label: 'Status', type: 'text', suggest: TUNNEL_STATUS },
            ]}
            onSave={(vpn) => save.mutate({ vpn })}
            saving={save.isPending}
            disabled={!online}
          />

          <Panel
            title="Addresses"
            sub={
              addressing.hosts.length > 0
                ? `${addressing.hosts.length} recorded across ${net.subnets.length} subnets`
                : 'Nothing in the register carries an address on these subnets yet'
            }
            flush
          >
            {/* The finding this panel exists for. Two machines on one address
                presents as "the network is flaky" and is invisible in a list
                sorted by anything but the address. */}
            {addressing.clashes.length > 0 && (
              <div className={s.ownedNote}>
                <i className="ti ti-alert-triangle" aria-hidden />
                <span>
                  {addressing.clashes.length === 1 ? 'One address is' : `${addressing.clashes.length} addresses are`}{' '}
                  recorded against more than one device:{' '}
                  {addressing.clashes
                    .map((c) => `${c.ip} (${c.devices.map((d) => d.tag).join(', ')})`)
                    .join('; ')}
                  .
                </span>
              </div>
            )}

            <DataTable
              rows={addressing.hosts}
              rowKey={(h) => `${h.subnet.cidr}-${h.device.id}`}
              initialSort={{ key: 'ip', dir: 'asc' }}
              columns={[
                {
                  key: 'ip',
                  /* No fixed width. 150px was narrower than the widest address
                     plus its copy button, so the column spent every row
                     wrapping to fit a number it was never given room for. Let
                     it ask for what it needs. */
                  header: 'Address',
                  // Already in address order from subnetUsage, which sorts
                  // numerically — a string sort would put .100 before .20.
                  sortBy: () => 0,
                  render: (h) => <Copyable value={h.ip} />,
                },
                {
                  key: 'device',
                  header: 'Device',
                  sortBy: (h) => h.device.tag,
                  render: (h) => (
                    <a className={s.cellMain} href={recordHref('/devices', h.device.tag)}>
                      {h.device.tag}
                    </a>
                  ),
                },
                {
                  key: 'type',
                  header: 'Type',
                  render: (h) => (
                    <span className={s.cellSub}>
                      {h.device.type}
                      {/* Which of the machine's two addresses this is. Without
                          it a server appears twice with no way to tell which
                          row is the one that answers when it is switched off. */}
                      {h.management && ` · ${h.device.mgmtType ?? 'management'}`}
                    </span>
                  ),
                },
                {
                  key: 'vlan',
                  header: 'Subnet',
                  sortBy: (h) => h.subnet.vlan,
                  render: (h) => (
                    <span className={s.cellSub}>
                      {h.subnet.vlan} · {h.subnet.name}
                    </span>
                  ),
                },
                {
                  key: 'how',
                  header: 'Assignment',
                  sortBy: (h) => (h.dynamic ? 1 : 0),
                  /* Static is the one that costs somebody something: it is a
                     decision that has to be maintained, and a machine holding an
                     address by hand inside a scope is a clash waiting to
                     happen. */
                  render: (h) =>
                    h.dynamic ? (
                      <Badge tone="muted">DHCP</Badge>
                    ) : (
                      <Badge tone="warn">Static</Badge>
                    ),
                },
              ]}
              empty={
                <EmptyState
                  icon="ti-list-numbers"
                  title="No addresses recorded"
                >
                  Devices carry an IP address; fill those in and this fills itself.
                </EmptyState>
              }
            />
          </Panel>

          <Panel
            title="Diagrams and plans"
            sub="Filed against this depot in the files library"
            flush
          >
<FileDrop
              recordId={`network-${depot}`}
              recordType="Network"
              hint="A network diagram, a comms room photo, a floor plan."
            />

            {(files.data ?? []).length > 0 && (
              <div className={s.rows}>
                {(files.data ?? []).map((f) => {
                  /* A link when the library gave us one, a plain row when it
                     did not — a file the app cannot open should not pretend to
                     be clickable. */
                  const inner = (
                    <>
                      <div className={s.rowIcon}>
                        <i className="ti ti-file" aria-hidden />
                      </div>
                      <div className={s.rowMain}>
                        <div className={s.rowTitle}>{f.name}</div>
                        <div className={s.rowSub}>
                          {f.size} · {f.by} · {fmtDate(f.when)}
                        </div>
                      </div>
                    </>
                  );
                  return f.url ? (
                    <a
                      key={f.name}
                      className={`${s.row} ${s.rowFlush}`}
                      href={f.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {inner}
                    </a>
                  ) : (
                    <div key={f.name} className={`${s.row} ${s.rowFlush}`}>
                      {inner}
                    </div>
                  );
                })}
              </div>
            )}
          </Panel>
        </div>
      )}
      <EditDrawer
        open={editing}
        record={net ?? null}
        fields={networkFields(valuesSeen(Object.values(networks.data ?? {}), ['provider', 'wanType', 'speed', 'dns']))}
        lookups={{ people: [], suppliers: [] }}
        noun="circuit"
        icon="ti-network"
        title={`${depotMeta?.name ?? depot} — circuit`}
        onClose={() => setEditing(false)}
        onSave={(values) => save.mutate(values as Partial<INetwork>)}
        saving={save.isPending}
        error={save.error}
      >
        <div className={s.ownedNote}>
          <i className="ti ti-device-floppy" aria-hidden />
          <span>
            One record per depot, so there is nothing to add or delete here — a depot's network
            either has details or is waiting for them. Subnets, SSIDs and tunnels are edited from
            their own panels.
          </span>
        </div>
      </EditDrawer>
    </>
  );
}
